import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Save, Phone, Mail, User, BookOpen, FileText, GraduationCap, Clock, MessageSquare, Folder, Activity, Pencil, Trash2, X, Plus, Upload, Download, CreditCard, IndianRupee, Users, Search, Crown, UserCheck, Award } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = ['INR', 'USD', 'GBP', 'EUR', 'AED'];
const YEARS = Array.from({ length: 30 }, (_, i) => String(new Date().getFullYear() + 2 - i));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_YEAR_OPTIONS = (() => {
  const yr = new Date().getFullYear();
  const opts = [];
  for (let y = yr; y <= yr + 2; y++) MONTHS.forEach(m => opts.push(`${m} ${y}`));
  return opts;
})();
const fmtIncome = (v) => { if (!v) return ''; const num = String(v).replace(/[^0-9]/g, ''); return num ? Number(num).toLocaleString('en-IN') : ''; };
const rawIncome = (v) => String(v).replace(/[^0-9]/g, '');

const CLOUD_COLORS = [
  'bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200',
  'bg-sky-50 text-sky-700 border-sky-200', 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-teal-50 text-teal-700 border-teal-200', 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'bg-indigo-50 text-indigo-700 border-indigo-200', 'bg-violet-50 text-violet-700 border-violet-200',
];

const STUDENT_SUGGESTIONS = ['Placement Assistance', 'Industry Exposure', 'Internship Opportunities', 'Research Projects', 'Hostel Facility', 'Scholarship', 'Sports Facilities', 'Campus Life', 'International Exposure', 'Skill Development', 'Coding Bootcamp', 'Entrepreneurship Support', 'Mentorship', 'Certification Courses'];
const PARENT_SUGGESTIONS = ['Good Placement Record', 'Safe Campus', 'Quality Faculty', 'Affordable Fees', 'Accreditation', 'Proximity to Home', 'Hostel Security', 'Career Guidance', 'Discipline', 'Reputation', 'Alumni Network', 'Transport Facility', 'Scholarship Options', 'Regular Updates'];

function PhraseCloud({ tags = [], label }) {
  const arr = Array.isArray(tags) ? tags : (tags ? String(tags).split(',').map(s => s.trim()).filter(Boolean) : []);
  if (!arr.length) return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-slate-900">—</p>
    </div>
  );
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-slate-500 uppercase">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {arr.map((t, i) => <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium border ${CLOUD_COLORS[i % CLOUD_COLORS.length]}`}>{t}</span>)}
      </div>
    </div>
  );
}

function EditablePhraseCloud({ tags = [], onChange, suggestions = [], label }) {
  const [input, setInput] = useState('');
  const arr = Array.isArray(tags) ? tags : (tags ? String(tags).split(',').map(s => s.trim()).filter(Boolean) : []);
  const addTag = (tag) => { const t = tag.trim(); if (t && !arr.includes(t)) onChange([...arr, t]); setInput(''); };
  const removeTag = (idx) => onChange(arr.filter((_, i) => i !== idx));
  const filtered = suggestions.filter(s => !arr.includes(s) && (!input || s.toLowerCase().includes(input.toLowerCase())));
  return (
    <div className="space-y-1.5 md:col-span-3">
      <Label className="text-xs font-semibold text-slate-500 uppercase">{label}</Label>
      <div className="flex flex-wrap gap-1.5 p-2 min-h-[40px] border border-slate-200 rounded-md bg-white">
        {arr.map((t, i) => (
          <span key={i} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${CLOUD_COLORS[i % CLOUD_COLORS.length]}`}>
            {t}<button type="button" onClick={() => removeTag(i)} className="ml-0.5 hover:opacity-70"><X size={12} /></button>
          </span>
        ))}
        <input className="flex-1 min-w-[120px] outline-none text-sm bg-transparent py-0.5" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && input.trim()) { e.preventDefault(); addTag(input); } }}
          placeholder={arr.length ? 'Add more...' : 'Type or select below...'} />
      </div>
      {filtered.length > 0 && <div className="flex flex-wrap gap-1">{filtered.slice(0, 8).map(s => (
        <button key={s} type="button" onClick={() => addTag(s)} className="px-2.5 py-0.5 rounded-full text-xs border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors">+ {s}</button>
      ))}</div>}
    </div>
  );
}

const STAGE_COLORS = {
  'Cold Lead': 'bg-blue-100 text-blue-800', 'Enquiry': 'bg-indigo-100 text-indigo-800',
  'Fee Paid': 'bg-green-100 text-green-800', 'Lost': 'bg-red-100 text-red-800',
  'Registration': 'bg-emerald-100 text-emerald-800',
};

export default function LeadProfilePage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [masters, setMasters] = useState({ courses: [], stages: [], sources: [], triggers: [], examTypes: [], users: [], lostReasons: [], subCourses: [], consultants: [] });
  const [parents, setParents] = useState([]);
  const [academics, setAcademics] = useState([]);
  const [examScores, setExamScores] = useState([]);
  const [coursePrefs, setCoursePrefs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [newNote, setNewNote] = useState({ content: '', note_type: 'general' });
  const [newFollowUp, setNewFollowUp] = useState({ follow_up_date: '', follow_up_type: 'call', next_action: '', remarks: '' });
  const [editingParentId, setEditingParentId] = useState(null);
  const [editParentForm, setEditParentForm] = useState({});
  const [editingAcademicId, setEditingAcademicId] = useState(null);
  const [editAcademicForm, setEditAcademicForm] = useState({});
  const [editingExamId, setEditingExamId] = useState(null);
  const [editExamForm, setEditExamForm] = useState({});
  const [editingCoursePrefId, setEditingCoursePrefId] = useState(null);
  const [editCoursePrefForm, setEditCoursePrefForm] = useState({});
  const [addAcademicDialog, setAddAcademicDialog] = useState(false);
  const [academicForm, setAcademicForm] = useState({ academic_level: 'class_10', school_name: '', board_name: '', passing_year: '', percentage_or_cgpa: '', stream: '', major_subjects: '' });
  const [addExamDialog, setAddExamDialog] = useState(false);
  const [examForm, setExamForm] = useState({ exam_type_id: '', score: '', rank: '', attempt_year: '' });
  const [addCoursePrefDialog, setAddCoursePrefDialog] = useState(false);
  const [coursePrefForm, setCoursePrefForm] = useState({ course_id: '', sub_course_id: '', preference_order: 1 });
  const [uploading, setUploading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('Other');
  const [paymentData, setPaymentData] = useState(null);
  const [addScheduleDialog, setAddScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ month_year: '', scheduled_amount: '', payment_mode: '', remarks: '' });
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editScheduleForm, setEditScheduleForm] = useState({});
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [scholarshipDialog, setScholarshipDialog] = useState(false);
  const [scholarshipForm, setScholarshipForm] = useState({ course_id: '', scholarship_amount: '', remarks: '', approved_by: '' });
  const [scholarshipHistory, setScholarshipHistory] = useState([]);
  const [paymentCourseEdit, setPaymentCourseEdit] = useState(false);
  const [addParentDialog, setAddParentDialog] = useState(false);
  const [parentForm, setParentForm] = useState({ relation_type: 'guardian', full_name: '', contact_number: '', email: '', occupation: '', annual_income: '' });
  const [groupData, setGroupData] = useState(null);
  const [groupRole, setGroupRole] = useState(null);
  const [groupEditing, setGroupEditing] = useState(false);
  const [groupForm, setGroupForm] = useState({ group_name: '', group_leader_lead_id: '', member_lead_ids: [], group_notes: '' });
  const [leadSearch, setLeadSearch] = useState('');
  const [leadSearchResults, setLeadSearchResults] = useState([]);
  const [leaderSearch, setLeaderSearch] = useState('');
  const [leaderSearchResults, setLeaderSearchResults] = useState([]);
  const [lostReasonModal, setLostReasonModal] = useState(false);
  const [lostReasonForm, setLostReasonForm] = useState({ lost_reason_id: '', lost_notes: '' });

  useEffect(() => {
    Promise.all([
      API.get('/masters/courses'), API.get('/masters/lead-stages'), API.get('/masters/lead-sources'),
      API.get('/masters/lead-triggers'), API.get('/masters/entrance-exam-types'), API.get('/users'),
      API.get('/masters/lost-reasons'), API.get('/sub-courses'), API.get('/consultants')
    ]).then(([c, st, so, tr, ex, u, lr, sc, con]) => setMasters({
      courses: c.data, stages: st.data, sources: so.data, triggers: tr.data, examTypes: ex.data, users: u.data, lostReasons: lr.data, subCourses: sc.data, consultants: con.data
    })).catch(() => {});
  }, []);

  const loadLead = () => {
    API.get(`/leads/${leadId}`).then(r => { setLead(r.data); setEditForm(r.data); }).catch(() => toast.error('Lead not found'));
  };
  const loadSubs = () => {
    API.get(`/leads/${leadId}/parents`).then(r => setParents(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/academics`).then(r => setAcademics(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/exam-scores`).then(r => setExamScores(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/course-preferences`).then(r => setCoursePrefs(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/notes`).then(r => setNotes(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/follow-ups`).then(r => setFollowUps(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/documents`).then(r => setDocuments(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/activity-logs`).then(r => setActivityLogs(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/payment`).then(r => setPaymentData(r.data)).catch(() => {});
    API.get('/masters/payment-modes').then(r => setPaymentModes(r.data)).catch(() => {});
    API.get('/masters/payment-statuses').then(r => setPaymentStatuses(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/payment/scholarship`).then(r => setScholarshipHistory(r.data)).catch(() => {});
    API.get(`/leads/${leadId}/group`).then(r => {
      setGroupData(r.data.group);
      setGroupRole(r.data.role);
    }).catch(() => {});
  };

  useEffect(() => { loadLead(); loadSubs(); }, [leadId]);

  const saveLead = async () => {
    // Check if stage is CHANGING to "Lost" — require reason
    const lostStage = masters.stages.find(s => s.name.toLowerCase() === 'lost');
    if (lostStage && editForm.current_stage_id === lostStage.id && lead.current_stage_id !== lostStage.id) {
      setLostReasonForm({ lost_reason_id: '', lost_notes: '' });
      setLostReasonModal(true);
      return;
    }
    try {
      await API.put(`/leads/${leadId}`, editForm);
      toast.success('Lead updated');
      setEditing(false);
      loadLead();
      loadSubs();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update';
      toast.error(msg);
    }
  };

  const confirmLostReason = async () => {
    if (!lostReasonForm.lost_reason_id) {
      toast.error('Please select a reason');
      return;
    }
    const updatedForm = { ...editForm, lost_reason_id: lostReasonForm.lost_reason_id, lost_notes: lostReasonForm.lost_notes };
    try {
      await API.put(`/leads/${leadId}`, updatedForm);
      toast.success('Lead marked as Lost');
      setEditing(false);
      setLostReasonModal(false);
      setLostReasonForm({ lost_reason_id: '', lost_notes: '' });
      loadLead();
      loadSubs();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update';
      toast.error(msg);
    }
  };

  const addNote = async () => {
    if (!newNote.content.trim()) return;
    await API.post(`/leads/${leadId}/notes`, newNote);
    toast.success('Note added');
    setNewNote({ content: '', note_type: 'general' });
    loadSubs();
  };

  const addFollowUp = async () => {
    if (!newFollowUp.follow_up_date) return;
    await API.post(`/leads/${leadId}/follow-ups`, newFollowUp);
    toast.success('Follow up scheduled');
    setNewFollowUp({ follow_up_date: '', follow_up_type: 'call', next_action: '', remarks: '' });
    loadSubs();
  };

  const markFollowUpComplete = async (id) => {
    await API.put(`/leads/${leadId}/follow-ups/${id}`, { status: 'completed' });
    toast.success('Marked complete');
    loadSubs();
  };

  const addParent = async () => {
    if (!parentForm.full_name) return;
    await API.post(`/leads/${leadId}/parents`, parentForm);
    toast.success('Parent added');
    setAddParentDialog(false);
    setParentForm({ relation_type: 'guardian', full_name: '', contact_number: '', email: '', occupation: '', annual_income: '' });
    loadSubs();
  };

  const startEditParent = (p) => { setEditingParentId(p.id); setEditParentForm({ ...p }); };
  const cancelEditParent = () => { setEditingParentId(null); setEditParentForm({}); };
  const saveParent = async () => {
    try {
      await API.put(`/leads/${leadId}/parents/${editingParentId}`, editParentForm);
      toast.success('Parent updated');
      cancelEditParent();
      loadSubs();
    } catch { toast.error('Failed to update parent'); }
  };
  const deleteParent = async (id) => {
    if (!window.confirm('Delete this parent record?')) return;
    await API.delete(`/leads/${leadId}/parents/${id}`);
    toast.success('Parent deleted');
    loadSubs();
  };

  const startEditAcademic = (a) => { setEditingAcademicId(a.id); setEditAcademicForm({ ...a }); };
  const cancelEditAcademic = () => { setEditingAcademicId(null); setEditAcademicForm({}); };
  const saveAcademic = async () => {
    try {
      await API.put(`/leads/${leadId}/academics/${editingAcademicId}`, editAcademicForm);
      toast.success('Academic record updated');
      cancelEditAcademic();
      loadSubs();
    } catch { toast.error('Failed to update'); }
  };
  const addAcademic = async () => {
    if (!academicForm.school_name && !academicForm.board_name) return;
    await API.post(`/leads/${leadId}/academics`, academicForm);
    toast.success('Academic record added');
    setAddAcademicDialog(false);
    setAcademicForm({ academic_level: 'class_10', school_name: '', board_name: '', passing_year: '', percentage_or_cgpa: '', stream: '', major_subjects: '' });
    loadSubs();
  };
  const deleteAcademic = async (id) => {
    if (!window.confirm('Delete this academic record?')) return;
    await API.delete(`/leads/${leadId}/academics/${id}`);
    toast.success('Deleted');
    loadSubs();
  };

  const startEditExam = (s) => { setEditingExamId(s.id); setEditExamForm({ ...s }); };
  const cancelEditExam = () => { setEditingExamId(null); setEditExamForm({}); };
  const saveExam = async () => {
    try {
      await API.put(`/leads/${leadId}/exam-scores/${editingExamId}`, editExamForm);
      toast.success('Exam score updated');
      cancelEditExam();
      loadSubs();
    } catch { toast.error('Failed to update'); }
  };
  const addExam = async () => {
    if (!examForm.exam_type_id) return;
    await API.post(`/leads/${leadId}/exam-scores`, examForm);
    toast.success('Exam score added');
    setAddExamDialog(false);
    setExamForm({ exam_type_id: '', score: '', rank: '', attempt_year: '' });
    loadSubs();
  };
  const deleteExam = async (id) => {
    if (!window.confirm('Delete this exam score?')) return;
    await API.delete(`/leads/${leadId}/exam-scores/${id}`);
    toast.success('Deleted');
    loadSubs();
  };

  const startEditCoursePref = (c) => { setEditingCoursePrefId(c.id); setEditCoursePrefForm({ ...c }); };
  const cancelEditCoursePref = () => { setEditingCoursePrefId(null); setEditCoursePrefForm({}); };
  const saveCoursePref = async () => {
    try {
      await API.put(`/leads/${leadId}/course-preferences/${editingCoursePrefId}`, editCoursePrefForm);
      toast.success('Course preference updated');
      cancelEditCoursePref();
      loadSubs();
    } catch { toast.error('Failed to update'); }
  };
  const addCoursePref = async () => {
    if (!coursePrefForm.course_id) return;
    await API.post(`/leads/${leadId}/course-preferences`, coursePrefForm);
    toast.success('Course preference added');
    setAddCoursePrefDialog(false);
    setCoursePrefForm({ course_id: '', sub_course_id: '', preference_order: 1 });
    loadSubs();
  };
  const deleteCoursePref = async (id) => {
    if (!window.confirm('Delete this course preference?')) return;
    await API.delete(`/leads/${leadId}/course-preferences/${id}`);
    toast.success('Deleted');
    loadSubs();
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await API.post(`/leads/${leadId}/documents/upload?document_type=${encodeURIComponent(uploadDocType)}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${files.length} file(s) uploaded`);
      loadSubs();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const resp = await API.get(`/leads/${leadId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await API.delete(`/leads/${leadId}/documents/${id}`);
    toast.success('Deleted');
    loadSubs();
  };

  const initPayment = async () => {
    const courseId = lead?.interested_course_id;
    if (!courseId) { toast.error('Select a course for this lead first'); return; }
    try {
      await API.post(`/leads/${leadId}/payment`, { course_id: courseId });
      toast.success('Payment plan created');
      loadSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addScheduleItem = async () => {
    if (!scheduleForm.scheduled_amount || !scheduleForm.month_year) { toast.error('Month/Year and amount required'); return; }
    try {
      await API.post(`/leads/${leadId}/payment/schedule`, scheduleForm);
      toast.success('Payment scheduled');
      setAddScheduleDialog(false);
      setScheduleForm({ month_year: '', scheduled_amount: '', payment_mode: '', remarks: '' });
      loadSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const startEditSchedule = (item) => { setEditingScheduleId(item.id); setEditScheduleForm({ ...item }); };
  const cancelEditSchedule = () => { setEditingScheduleId(null); setEditScheduleForm({}); };
  const saveScheduleItem = async () => {
    try {
      await API.put(`/leads/${leadId}/payment/schedule/${editingScheduleId}`, editScheduleForm);
      toast.success('Updated');
      cancelEditSchedule();
      loadSubs();
    } catch { toast.error('Failed to update'); }
  };
  const deleteScheduleItem = async (id) => {
    if (!window.confirm('Delete this schedule item?')) return;
    await API.delete(`/leads/${leadId}/payment/schedule/${id}`);
    toast.success('Deleted');
    loadSubs();
  };

  const openScholarshipDialog = () => {
    const courseId = paymentData?.course_id || lead?.interested_course_id || '';
    const course = masters.courses.find(c => c.id === courseId);
    const courseFees = Number(course?.annual_fees || 0);
    setScholarshipForm({
      course_id: courseId,
      original_fees: courseFees,
      scholarship_amount: '',
      remarks: '',
      approved_by: '',
    });
    setScholarshipDialog(true);
  };

  const applyScholarship = async () => {
    const amt = parseFloat(scholarshipForm.scholarship_amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid scholarship amount'); return; }
    try {
      await API.post(`/leads/${leadId}/payment/scholarship`, {
        course_id: scholarshipForm.course_id,
        scholarship_amount: amt,
        remarks: scholarshipForm.remarks,
        approved_by: scholarshipForm.approved_by || undefined,
      });
      toast.success('Scholarship applied');
      setScholarshipDialog(false);
      loadSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const changePaymentCourse = async (courseId) => {
    try {
      await API.put(`/leads/${leadId}`, { interested_course_id: courseId });
      toast.success('Course updated — payment plan synced');
      setPaymentCourseEdit(false);
      loadLead();
      loadSubs();
    } catch { toast.error('Failed to update course'); }
  };

  // Group Booking functions
  const searchLeads = async (q, setter) => {
    if (q.length < 2) { setter([]); return; }
    try {
      const r = await API.get(`/leads/search-simple?q=${encodeURIComponent(q)}`);
      setter(r.data);
    } catch { setter([]); }
  };

  const createGroup = async () => {
    if (!groupForm.group_name.trim()) { toast.error('Group name is required'); return; }
    try {
      const memberIds = groupForm.member_lead_ids.includes(leadId)
        ? groupForm.member_lead_ids : [...groupForm.member_lead_ids, leadId];
      await API.post('/groups', {
        ...groupForm,
        group_leader_lead_id: groupForm.group_leader_lead_id || leadId,
        member_lead_ids: memberIds,
      });
      toast.success('Group created');
      setGroupEditing(false);
      loadSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateGroup = async () => {
    if (!groupData?.id) return;
    try {
      await API.put(`/groups/${groupData.id}`, groupForm);
      toast.success('Group updated');
      setGroupEditing(false);
      loadSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteGroup = async () => {
    if (!groupData?.id || !window.confirm('Delete this group? All members will be unlinked.')) return;
    await API.delete(`/groups/${groupData.id}`);
    toast.success('Group deleted');
    setGroupData(null);
    setGroupRole(null);
    setGroupEditing(false);
    loadSubs();
  };

  const addGroupMember = async (lid) => {
    if (!groupData?.id) return;
    try {
      await API.post(`/groups/${groupData.id}/members`, { lead_ids: [lid] });
      toast.success('Member added');
      setLeadSearch('');
      setLeadSearchResults([]);
      loadSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeGroupMember = async (lid) => {
    if (!groupData?.id || !window.confirm('Remove this member from the group?')) return;
    await API.delete(`/groups/${groupData.id}/members/${lid}`);
    toast.success('Member removed');
    loadSubs();
  };

  const startGroupEdit = () => {
    setGroupEditing(true);
    if (groupData) {
      setGroupForm({
        group_name: groupData.group_name || '',
        group_leader_lead_id: groupData.group_leader_lead_id || '',
        member_lead_ids: groupData.member_lead_ids || [],
        group_notes: groupData.group_notes || '',
      });
    } else {
      setGroupForm({ group_name: '', group_leader_lead_id: leadId, member_lead_ids: [leadId], group_notes: '' });
    }
  };

  if (!lead) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-8 bg-slate-200 rounded w-48" /><div className="h-64 bg-slate-100 rounded" /></div></div>;

  const F = ({ label, value }) => (
    <div className="space-y-1"><p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p><p className="text-sm text-slate-900">{value || '-'}</p></div>
  );

  const EF = ({ label, field, type = 'text' }) => (
    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</Label>
      <Input type={type} value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))} /></div>
  );

  return (
    <div className="p-6 space-y-4" data-testid="lead-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="back-btn"><ArrowLeft size={16} className="mr-1" /> Back</Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{lead.full_name}</h1>
              <Badge className={`text-xs ${STAGE_COLORS[lead.stage_name] || 'bg-slate-100 text-slate-700'}`}>{lead.stage_name}</Badge>
            </div>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{lead.lead_uid} &middot; {lead.course_name || 'No course'} &middot; {lead.source_name || 'No source'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {lead.phone_number && <Button variant="outline" size="sm" onClick={() => window.open(`https://wa.me/${lead.phone_number.replace(/\D/g,'')}`, '_blank')} data-testid="whatsapp-btn"><Phone size={14} className="mr-1" /> WhatsApp</Button>}
          {lead.email && <Button variant="outline" size="sm" onClick={() => window.open(`mailto:${lead.email}`)} data-testid="email-btn"><Mail size={14} className="mr-1" /> Email</Button>}
          {!editing ? (
            <Button size="sm" onClick={() => setEditing(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-lead-btn">Edit Lead</Button>
          ) : (
            <Button size="sm" onClick={saveLead} className="bg-slate-900 hover:bg-slate-800" data-testid="save-lead-btn"><Save size={14} className="mr-1" /> Save</Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
          {[
            { val: 'personal', icon: User, label: 'Personal' },
            { val: 'parents', icon: User, label: 'Parents' },
            { val: 'academic', icon: GraduationCap, label: 'Academic' },
            { val: 'exams', icon: FileText, label: 'Exams' },
            { val: 'courses', icon: BookOpen, label: 'Courses' },
            { val: 'source', icon: Activity, label: 'Source' },
            { val: 'notes', icon: MessageSquare, label: 'Notes' },
            { val: 'followups', icon: Clock, label: 'Follow Ups' },
            { val: 'documents', icon: Folder, label: 'Documents' },
            { val: 'payment', icon: CreditCard, label: 'Payment' },
            { val: 'group', icon: Users, label: 'Group' },
            { val: 'activity', icon: Activity, label: 'Activity' },
          ].map(t => (
            <TabsTrigger key={t.val} value={t.val} className="text-xs gap-1.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid={`tab-${t.val}`}>
              <t.icon size={13} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Personal Tab */}
        <TabsContent value="personal" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="pt-6">
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <EF label="First Name" field="first_name" /><EF label="Last Name" field="last_name" />
                  <EF label="Phone" field="phone_number" /><EF label="Email" field="email" type="email" />
                  <EF label="Alt Phone" field="alternate_phone_number" /><EF label="Gender" field="gender" />
                  <EF label="Date of Birth" field="date_of_birth" type="date" /><EF label="Age" field="age" type="number" />
                  <EF label="Aadhaar" field="aadhaar_number" />
                  <EF label="Address Line 1" field="address_line_1" /><EF label="Address Line 2" field="address_line_2" />
                  <EF label="City" field="city" /><EF label="State" field="state" /><EF label="Pin Code" field="pin_code" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <F label="Full Name" value={lead.full_name} /><F label="Phone" value={lead.phone_number} />
                  <F label="Email" value={lead.email} /><F label="Gender" value={lead.gender} />
                  <F label="DOB" value={lead.date_of_birth} /><F label="Age" value={lead.age} />
                  <F label="Aadhaar" value={lead.aadhaar_number} /><F label="Alt Phone" value={lead.alternate_phone_number} />
                  <F label="Address" value={[lead.address_line_1, lead.address_line_2].filter(Boolean).join(', ')} />
                  <F label="City" value={lead.city} /><F label="State" value={lead.state} /><F label="Pin Code" value={lead.pin_code} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Parents Tab */}
        <TabsContent value="parents" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => setAddParentDialog(true)} data-testid="add-parent-btn"><Plus size={14} className="mr-1" /> Add Parent</Button></div>
          {parents.map(p => (
            <Card key={p.id} className="border-slate-200">
              {editingParentId === p.id ? (
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Relation</Label>
                      <Select value={editParentForm.relation_type || 'guardian'} onValueChange={v => setEditParentForm(f => ({...f, relation_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="father">Father</SelectItem><SelectItem value="mother">Mother</SelectItem><SelectItem value="guardian">Guardian</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Full Name</Label><Input value={editParentForm.full_name || ''} onChange={e => setEditParentForm(f => ({...f, full_name: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Contact</Label><Input value={editParentForm.contact_number || ''} onChange={e => setEditParentForm(f => ({...f, contact_number: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Email</Label><Input type="email" value={editParentForm.email || ''} onChange={e => setEditParentForm(f => ({...f, email: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Occupation</Label><Input value={editParentForm.occupation || ''} onChange={e => setEditParentForm(f => ({...f, occupation: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Annual Income</Label>
                      <div className="flex gap-1">
                        <Select value={editParentForm.income_currency || 'INR'} onValueChange={v => setEditParentForm(f => ({...f, income_currency: v}))}>
                          <SelectTrigger className="w-24 shrink-0"><SelectValue /></SelectTrigger>
                          <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={fmtIncome(editParentForm.annual_income)} onChange={e => setEditParentForm(f => ({...f, annual_income: rawIncome(e.target.value)}))} placeholder="e.g. 10,00,000" />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={cancelEditParent}><X size={14} className="mr-1" /> Cancel</Button>
                    <Button size="sm" onClick={saveParent} className="bg-slate-900 hover:bg-slate-800"><Save size={14} className="mr-1" /> Save</Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                      <F label="Relation" value={p.relation_type} /><F label="Name" value={p.full_name} />
                      <F label="Contact" value={p.contact_number} /><F label="Email" value={p.email} />
                      <F label="Occupation" value={p.occupation} /><F label="Income" value={p.annual_income} />
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEditParent(p)} data-testid={`edit-parent-${p.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteParent(p.id)} data-testid={`delete-parent-${p.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
          {parents.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No parent details added.</p>}
        </TabsContent>

        {/* Academic Tab */}
        <TabsContent value="academic" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => setAddAcademicDialog(true)} data-testid="add-academic-btn"><Plus size={14} className="mr-1" /> Add Academic</Button></div>
          {academics.map(a => (
            <Card key={a.id} className="border-slate-200">
              {editingAcademicId === a.id ? (
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Level</Label>
                      <Select value={editAcademicForm.academic_level || 'class_10'} onValueChange={v => setEditAcademicForm(f => ({...f, academic_level: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="class_10">Class 10</SelectItem><SelectItem value="class_12">Class 12</SelectItem><SelectItem value="graduation">Graduation</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">School</Label><Input value={editAcademicForm.school_name || ''} onChange={e => setEditAcademicForm(f => ({...f, school_name: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Board</Label><Input value={editAcademicForm.board_name || ''} onChange={e => setEditAcademicForm(f => ({...f, board_name: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Year</Label>
                      <Select value={editAcademicForm.passing_year || 'unset'} onValueChange={v => setEditAcademicForm(f => ({...f, passing_year: v === 'unset' ? '' : v}))}>
                        <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                        <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">%/CGPA</Label><Input value={editAcademicForm.percentage_or_cgpa || ''} onChange={e => setEditAcademicForm(f => ({...f, percentage_or_cgpa: e.target.value}))} /></div>
                    {editAcademicForm.academic_level !== 'class_10' && (
                      <>
                        <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Stream</Label><Input value={editAcademicForm.stream || ''} onChange={e => setEditAcademicForm(f => ({...f, stream: e.target.value}))} /></div>
                        <div className="space-y-1.5 md:col-span-3"><Label className="text-xs font-semibold text-slate-500 uppercase">Major Subjects</Label><Input value={editAcademicForm.major_subjects || ''} onChange={e => setEditAcademicForm(f => ({...f, major_subjects: e.target.value}))} /></div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={cancelEditAcademic}><X size={14} className="mr-1" /> Cancel</Button>
                    <Button size="sm" onClick={saveAcademic} className="bg-slate-900 hover:bg-slate-800"><Save size={14} className="mr-1" /> Save</Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-3">{a.academic_level === 'class_10' ? 'Class 10' : a.academic_level === 'class_12' ? 'Class 12' : 'Graduation'}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <F label="School" value={a.school_name} /><F label="Board" value={a.board_name} />
                        <F label="Year" value={a.passing_year} /><F label="%" value={a.percentage_or_cgpa} />
                        {a.academic_level !== 'class_10' && <><F label="Stream" value={a.stream} /><F label="Subjects" value={a.major_subjects} /></>}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEditAcademic(a)} data-testid={`edit-academic-${a.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteAcademic(a.id)} data-testid={`delete-academic-${a.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
          {academics.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No academic details.</p>}
        </TabsContent>

        {/* Exams Tab */}
        <TabsContent value="exams" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => setAddExamDialog(true)} data-testid="add-exam-profile-btn"><Plus size={14} className="mr-1" /> Add Exam</Button></div>
          {examScores.map(s => (
            <Card key={s.id} className="border-slate-200">
              {editingExamId === s.id ? (
                <CardContent className="pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Exam Type</Label>
                      <Select value={editExamForm.exam_type_id || 'unset'} onValueChange={v => setEditExamForm(f => ({...f, exam_type_id: v === 'unset' ? '' : v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.examTypes.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Score</Label><Input value={editExamForm.score || ''} onChange={e => setEditExamForm(f => ({...f, score: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Rank</Label><Input value={editExamForm.rank || ''} onChange={e => setEditExamForm(f => ({...f, rank: e.target.value}))} /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Year</Label>
                      <Select value={editExamForm.attempt_year || 'unset'} onValueChange={v => setEditExamForm(f => ({...f, attempt_year: v === 'unset' ? '' : v}))}>
                        <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                        <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={cancelEditExam}><X size={14} className="mr-1" /> Cancel</Button>
                    <Button size="sm" onClick={saveExam} className="bg-slate-900 hover:bg-slate-800"><Save size={14} className="mr-1" /> Save</Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                      <F label="Exam" value={s.exam_type_name} /><F label="Score" value={s.score} />
                      <F label="Rank" value={s.rank} /><F label="Year" value={s.attempt_year} />
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEditExam(s)} data-testid={`edit-exam-${s.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteExam(s.id)} data-testid={`delete-exam-${s.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
          {examScores.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No exam scores.</p>}
        </TabsContent>

        {/* Course Preferences Tab */}
        <TabsContent value="courses" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => setAddCoursePrefDialog(true)} data-testid="add-course-pref-profile-btn"><Plus size={14} className="mr-1" /> Add Course</Button></div>
          {[...coursePrefs].sort((a, b) => (a.preference_order || 99) - (b.preference_order || 99)).map(c => {
            const prefLabel = c.preference_order === 1 ? 'Primary Course Preference' : c.preference_order === 2 ? 'Secondary Course Preference' : `Preference #${c.preference_order}`;
            return (
            <Card key={c.id} className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-xs font-semibold text-slate-500 uppercase">{prefLabel}</CardTitle></CardHeader>
              {editingCoursePrefId === c.id ? (
                <CardContent className="pt-2 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Course</Label>
                      <Select value={editCoursePrefForm.course_id || 'unset'} onValueChange={v => setEditCoursePrefForm(f => ({...f, course_id: v === 'unset' ? '' : v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.courses.map(co => <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Sub-Course</Label>
                      <Select value={editCoursePrefForm.sub_course_id || 'unset'} onValueChange={v => setEditCoursePrefForm(f => ({...f, sub_course_id: v === 'unset' ? '' : v}))} disabled={!editCoursePrefForm.course_id}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">None</SelectItem>{(masters.subCourses || []).filter(s => s.course_id === editCoursePrefForm.course_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Priority</Label>
                      <Select value={String(editCoursePrefForm.preference_order || 1)} onValueChange={v => setEditCoursePrefForm(f => ({...f, preference_order: parseInt(v)}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="1">Primary</SelectItem><SelectItem value="2">Secondary</SelectItem><SelectItem value="3">Tertiary</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={cancelEditCoursePref}><X size={14} className="mr-1" /> Cancel</Button>
                    <Button size="sm" onClick={saveCoursePref} className="bg-slate-900 hover:bg-slate-800"><Save size={14} className="mr-1" /> Save</Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-2">
                  <div className="flex items-start justify-between">
                    <div className="grid grid-cols-2 gap-4 flex-1">
                      <F label="Course" value={c.course_name} />
                      <F label="Sub-Course" value={c.sub_course_name || '—'} />
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEditCoursePref(c)} data-testid={`edit-course-${c.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteCoursePref(c.id)} data-testid={`delete-course-${c.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
            );
          })}
          {coursePrefs.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No course preferences.</p>}
        </TabsContent>

        {/* Source & Trigger Tab */}
        <TabsContent value="source" className="mt-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Primary Source</Label>
                    <Select value={editForm.source_id || "unset"} onValueChange={v => setEditForm(p => ({...p, source_id: v === 'unset' ? '' : v, primary_source_consultant_id: ''}))}>
                      <SelectTrigger data-testid="edit-primary-source"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{masters.sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {masters.sources.find(s => s.id === editForm.source_id)?.name?.toLowerCase().includes('consultant') && (
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Primary Consultant</Label>
                      <Select value={editForm.primary_source_consultant_id || "unset"} onValueChange={v => setEditForm(p => ({...p, primary_source_consultant_id: v === 'unset' ? '' : v}))}>
                        <SelectTrigger data-testid="edit-primary-consultant"><SelectValue placeholder="Select Consultant" /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">None</SelectItem>{masters.consultants.filter(c => c.is_active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Secondary Source</Label>
                    <Select value={editForm.secondary_source_id || "unset"} onValueChange={v => setEditForm(p => ({...p, secondary_source_id: v === 'unset' ? '' : v, secondary_source_consultant_id: ''}))}>
                      <SelectTrigger data-testid="edit-secondary-source"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{masters.sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {masters.sources.find(s => s.id === editForm.secondary_source_id)?.name?.toLowerCase().includes('consultant') && (
                    <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Secondary Consultant</Label>
                      <Select value={editForm.secondary_source_consultant_id || "unset"} onValueChange={v => setEditForm(p => ({...p, secondary_source_consultant_id: v === 'unset' ? '' : v}))}>
                        <SelectTrigger data-testid="edit-secondary-consultant"><SelectValue placeholder="Select Consultant" /></SelectTrigger>
                        <SelectContent><SelectItem value="unset">None</SelectItem>{masters.consultants.filter(c => c.is_active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Trigger</Label>
                    <Select value={editForm.trigger_id || "unset"} onValueChange={v => setEditForm(p => ({...p, trigger_id: v === 'unset' ? '' : v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{masters.triggers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Counsellor</Label>
                    <Select value={editForm.assigned_counsellor_id || "unset"} onValueChange={v => setEditForm(p => ({...p, assigned_counsellor_id: v === 'unset' ? '' : v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{masters.users.filter(u => ['admin', 'counsellor', 'admission_head'].includes(u.role)).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Interested Course</Label>
                    <Select value={editForm.interested_course_id || "unset"} onValueChange={v => setEditForm(p => ({...p, interested_course_id: v === 'unset' ? '' : v, interested_sub_course_id: ''}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Sub-Course / Specialization</Label>
                    <Select value={editForm.interested_sub_course_id || "unset"} onValueChange={v => setEditForm(p => ({...p, interested_sub_course_id: v === 'unset' ? '' : v}))} disabled={!editForm.interested_course_id}>
                      <SelectTrigger><SelectValue placeholder={editForm.interested_course_id ? "Select" : "Select course first"} /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{(masters.subCourses || []).filter(s => s.course_id === editForm.interested_course_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-500 uppercase">Stage</Label>
                    <Select value={editForm.current_stage_id || "unset"} onValueChange={v => setEditForm(p => ({...p, current_stage_id: v === 'unset' ? '' : v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unset">None</SelectItem>{masters.stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Separator className="my-2" />
                  <EditablePhraseCloud label="Student Expectations" tags={editForm.student_expectations} onChange={v => setEditForm(p => ({...p, student_expectations: v}))} suggestions={STUDENT_SUGGESTIONS} />
                  <EditablePhraseCloud label="Parent Expectations" tags={editForm.parent_expectations} onChange={v => setEditForm(p => ({...p, parent_expectations: v}))} suggestions={PARENT_SUGGESTIONS} />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <F label="Primary Source" value={lead.source_name} />
                    {lead.primary_consultant_name && <F label="Primary Consultant" value={lead.primary_consultant_name} />}
                    <F label="Secondary Source" value={lead.secondary_source_name} />
                    {lead.secondary_consultant_name && <F label="Secondary Consultant" value={lead.secondary_consultant_name} />}
                    <F label="Trigger" value={lead.trigger_name} />
                    <F label="Counsellor" value={lead.counsellor_name} /><F label="Course" value={lead.course_name} />
                    <F label="Sub-Course" value={lead.sub_course_name} /><F label="Stage" value={lead.stage_name} />
                  </div>
                  <Separator />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <PhraseCloud label="Student Expectations" tags={lead.student_expectations} />
                    <PhraseCloud label="Parent Expectations" tags={lead.parent_expectations} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4 space-y-3">
              <div className="flex gap-2">
                <Select value={newNote.note_type} onValueChange={v => setNewNote(p => ({...p, note_type: v}))}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem><SelectItem value="call_note">Call Note</SelectItem>
                    <SelectItem value="counselling_note">Counselling</SelectItem><SelectItem value="follow_up_note">Follow Up</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea className="flex-1" rows={2} placeholder="Add a note..." value={newNote.content} onChange={e => setNewNote(p => ({...p, content: e.target.value}))} data-testid="note-input" />
              </div>
              <Button size="sm" onClick={addNote} className="bg-slate-900 hover:bg-slate-800" data-testid="add-note-btn">Add Note</Button>
            </CardContent>
          </Card>
          {notes.map(n => (
            <div key={n.id} className="p-4 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <Badge variant="secondary" className="text-xs capitalize">{n.note_type?.replace(/_/g, ' ')}</Badge>
                <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()} &middot; {n.created_by_name}</span>
              </div>
              <p className="text-sm text-slate-700">{n.content}</p>
            </div>
          ))}
        </TabsContent>

        {/* Follow Ups Tab */}
        <TabsContent value="followups" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input type="date" value={newFollowUp.follow_up_date} onChange={e => setNewFollowUp(p => ({...p, follow_up_date: e.target.value}))} data-testid="new-followup-date" />
                <Select value={newFollowUp.follow_up_type} onValueChange={v => setNewFollowUp(p => ({...p, follow_up_type: v}))}>
                  <SelectTrigger data-testid="followup-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Next action..." value={newFollowUp.next_action} onChange={e => setNewFollowUp(p => ({...p, next_action: e.target.value}))} />
                <Input placeholder="Remarks..." value={newFollowUp.remarks} onChange={e => setNewFollowUp(p => ({...p, remarks: e.target.value}))} />
              </div>
              <Button size="sm" onClick={addFollowUp} className="bg-slate-900 hover:bg-slate-800" data-testid="add-followup-btn">Schedule Follow Up</Button>
            </CardContent>
          </Card>
          {followUps.map(f => (
            <div key={f.id} className={`p-4 bg-white rounded-lg border ${f.status === 'completed' ? 'border-green-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">{f.follow_up_date} {f.follow_up_time && `at ${f.follow_up_time}`}</p>
                    {f.follow_up_type && <Badge className="text-xs bg-blue-50 text-blue-700 capitalize">{f.follow_up_type}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{f.next_action}</p>
                  {f.remarks && <p className="text-xs text-slate-400 mt-1">{f.remarks}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${f.status === 'completed' ? 'bg-green-100 text-green-800' : f.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{f.status}</Badge>
                  {f.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => markFollowUpComplete(f.id)} data-testid={`complete-followup-${f.id}`}>Complete</Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={uploadDocType} onValueChange={setUploadDocType}>
                  <SelectTrigger className="w-44" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aadhar Card">Aadhar Card</SelectItem>
                    <SelectItem value="PAN Card">PAN Card</SelectItem>
                    <SelectItem value="Class 10 Marksheet">Class 10 Marksheet</SelectItem>
                    <SelectItem value="Class 12 Marksheet">Class 12 Marksheet</SelectItem>
                    <SelectItem value="Transfer Certificate">Transfer Certificate</SelectItem>
                    <SelectItem value="Migration Certificate">Migration Certificate</SelectItem>
                    <SelectItem value="Passport Photo">Passport Photo</SelectItem>
                    <SelectItem value="Entrance Exam Scorecard">Entrance Exam Scorecard</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" multiple onChange={handleFileUpload} disabled={uploading} data-testid="doc-file-input" />
                  <div className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white ${uploading ? 'bg-slate-400' : 'bg-slate-900 hover:bg-slate-800'}`}>
                    <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload File'}
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>
          {documents.map(d => (
            <div key={d.id} className="p-4 bg-white rounded-lg border border-slate-200 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">{d.file_name}</p>
                <p className="text-xs text-slate-400">{d.document_type} &middot; {d.size ? `${(d.size / 1024).toFixed(1)} KB` : ''} &middot; {new Date(d.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-1">
                {d.storage_path && <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`}><Download size={14} /></Button>}
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteDoc(d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
          {documents.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No documents uploaded. Use the button above to upload files.</p>}
        </TabsContent>


        {/* Payment Tab */}
        <TabsContent value="payment" className="mt-4 space-y-4">
          {!paymentData?.id ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <IndianRupee size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="text-sm text-slate-500 mb-3">No payment plan created yet.{!lead?.interested_course_id && ' Select a course for this lead first.'}</p>
                <Button onClick={initPayment} className="bg-slate-900 hover:bg-slate-800" data-testid="init-payment-btn" disabled={!lead?.interested_course_id}>
                  <Plus size={14} className="mr-1" /> Create Payment Plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Course Card - Editable */}
                <Card className="border-slate-200 group relative cursor-pointer" onClick={() => setPaymentCourseEdit(true)} data-testid="payment-course-card">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Course <Pencil size={10} className="inline ml-1 opacity-0 group-hover:opacity-100 text-slate-400" /></p>
                    <p className="text-sm font-semibold text-slate-900">{paymentData.course_name}</p>
                  </CardContent>
                </Card>
                {[
                  { label: 'Total Fees (Year 1)', value: `₹${Number(paymentData.total_fees_year_1 || 0).toLocaleString('en-IN')}`, type: 'text' },
                  { label: 'Total Scheduled', value: `₹${Number(paymentData.total_amount_scheduled || 0).toLocaleString('en-IN')}`, type: 'text' },
                  { label: 'Total Paid', value: `₹${Number(paymentData.total_amount_paid || 0).toLocaleString('en-IN')}`, color: 'text-green-700' },
                  { label: 'Balance Left', value: `₹${Number(paymentData.total_payment_left || 0).toLocaleString('en-IN')}`, color: paymentData.total_payment_left > 0 ? 'text-amber-700' : 'text-green-700' },
                  { label: 'Status', value: paymentData.overall_payment_status?.replace('_', ' '), type: 'badge' },
                ].map((s, i) => (
                  <Card key={i} className="border-slate-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">{s.label}</p>
                      {s.type === 'badge' ? (
                        <Badge className={`text-xs capitalize ${s.value === 'paid' ? 'bg-green-100 text-green-800' : s.value === 'overdue' ? 'bg-red-100 text-red-800' : s.value === 'partially paid' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>{s.value}</Badge>
                      ) : (
                        <p className={`text-sm font-semibold ${s.color || 'text-slate-900'}`}>{s.value}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              {/* Scholarship Info + Button */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {paymentData.scholarship_amount > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <Award size={14} className="text-emerald-600" />
                      <span className="text-xs text-emerald-800">
                        Scholarship: <span className="font-semibold">₹{Number(paymentData.scholarship_amount).toLocaleString('en-IN')}</span>
                        <span className="text-emerald-500 ml-1">(Course Fees: ₹{Number(paymentData.annual_fees || paymentData.original_fees || 0).toLocaleString('en-IN')})</span>
                      </span>
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={openScholarshipDialog} data-testid="scholarship-btn">
                  <Award size={14} /> Scholarship
                </Button>
              </div>
              {paymentData.total_amount_scheduled > 0 && paymentData.total_amount_scheduled !== paymentData.total_fees_year_1 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  Total scheduled (₹{Number(paymentData.total_amount_scheduled).toLocaleString('en-IN')}) does not match total fees (₹{Number(paymentData.total_fees_year_1).toLocaleString('en-IN')}).
                </div>
              )}
              {/* Schedule Table */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-semibold">Payment Schedule</CardTitle>
                  <Button size="sm" onClick={() => setAddScheduleDialog(true)} data-testid="add-schedule-btn"><Plus size={14} className="mr-1" /> Add Row</Button>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 border-b">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Month/Year</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Scheduled</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Received</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Remaining</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Mode</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Remarks</th>
                        <th className="px-3 py-2 w-20"></th>
                      </tr></thead>
                      <tbody>
                        {(paymentData.schedule_items || []).map(item => (
                          editingScheduleId === item.id ? (
                            <tr key={item.id} className="border-b bg-blue-50/30">
                              <td className="px-2 py-1.5">
                                <Select value={editScheduleForm.month_year || 'none'} onValueChange={v => setEditScheduleForm(f => ({...f, month_year: v === 'none' ? '' : v}))}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                                  <SelectContent>{MONTH_YEAR_OPTIONS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1.5"><Input type="number" className="h-8 text-xs text-right" value={editScheduleForm.scheduled_amount || ''} onChange={e => setEditScheduleForm(f => ({...f, scheduled_amount: parseFloat(e.target.value) || 0}))} /></td>
                              <td className="px-2 py-1.5"><Input type="number" className="h-8 text-xs text-right" value={editScheduleForm.received_amount || ''} onChange={e => setEditScheduleForm(f => ({...f, received_amount: parseFloat(e.target.value) || 0}))} /></td>
                              <td className="px-2 py-1.5 text-right text-xs text-slate-500">₹{((editScheduleForm.scheduled_amount || 0) - (editScheduleForm.received_amount || 0)).toLocaleString('en-IN')}</td>
                              <td className="px-2 py-1.5">
                                <Select value={editScheduleForm.payment_mode || 'none'} onValueChange={v => setEditScheduleForm(f => ({...f, payment_mode: v === 'none' ? '' : v}))}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent><SelectItem value="none">—</SelectItem>{paymentModes.map(m => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1.5">
                                <Select value={editScheduleForm.payment_status || 'pending'} onValueChange={v => setEditScheduleForm(f => ({...f, payment_status: v}))}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>{paymentStatuses.map(s => <SelectItem key={s.id} value={s.name.toLowerCase().replace(/ /g, '_')}>{s.name}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1.5"><Input type="date" className="h-8 text-xs" value={editScheduleForm.payment_date || ''} onChange={e => setEditScheduleForm(f => ({...f, payment_date: e.target.value}))} /></td>
                              <td className="px-2 py-1.5"><Input className="h-8 text-xs" value={editScheduleForm.remarks || ''} onChange={e => setEditScheduleForm(f => ({...f, remarks: e.target.value}))} /></td>
                              <td className="px-2 py-1.5">
                                <div className="flex gap-0.5">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={saveScheduleItem}><Save size={12} /></Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={cancelEditSchedule}><X size={12} /></Button>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <tr key={item.id} className="border-b hover:bg-slate-50">
                              <td className="px-3 py-2.5 font-medium text-slate-900">{item.month_year}</td>
                              <td className="px-3 py-2.5 text-right">₹{Number(item.scheduled_amount || 0).toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2.5 text-right text-green-700">₹{Number(item.received_amount || 0).toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2.5 text-right text-amber-700">₹{Number(item.payment_left || 0).toLocaleString('en-IN')}</td>
                              <td className="px-3 py-2.5 text-slate-600">{item.payment_mode || '—'}</td>
                              <td className="px-3 py-2.5">
                                <Badge className={`text-xs capitalize ${item.payment_status === 'paid' ? 'bg-green-100 text-green-800' : item.payment_status === 'overdue' ? 'bg-red-100 text-red-800' : item.payment_status === 'partially_paid' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>{(item.payment_status || 'pending').replace('_', ' ')}</Badge>
                              </td>
                              <td className="px-3 py-2.5 text-xs text-slate-500">{item.payment_date || '—'}</td>
                              <td className="px-3 py-2.5 text-xs text-slate-400 max-w-[100px] truncate">{item.remarks || ''}</td>
                              <td className="px-2 py-2.5">
                                <div className="flex gap-0.5">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEditSchedule(item)}><Pencil size={12} /></Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteScheduleItem(item.id)}><Trash2 size={12} /></Button>
                                </div>
                              </td>
                            </tr>
                          )
                        ))}
                        {(!paymentData.schedule_items || paymentData.schedule_items.length === 0) && (
                          <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-slate-400">No payment schedule rows. Click "Add Row" to start.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        {/* Group Tab */}
        <TabsContent value="group" className="mt-4 space-y-4">
          {!groupData && !groupEditing ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Users size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="text-sm text-slate-500 mb-3">No group booking linked to this lead.</p>
                <Button onClick={startGroupEdit} className="bg-slate-900 hover:bg-slate-800" data-testid="create-group-btn">
                  <Plus size={14} className="mr-1" /> Create Group
                </Button>
              </CardContent>
            </Card>
          ) : groupEditing ? (
            <Card className="border-slate-200">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold">{groupData ? 'Edit Group' : 'Create Group'}</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setGroupEditing(false)}><X size={14} /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Group Name *</Label>
                    <Input value={groupForm.group_name} onChange={e => setGroupForm(f => ({...f, group_name: e.target.value}))} placeholder="e.g. Sharma Family, Delhi Group" data-testid="group-name-input" />
                  </div>
                  <div className="space-y-1.5 relative">
                    <Label className="text-xs font-semibold text-slate-500 uppercase">Group Leader</Label>
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                      <Input className="pl-8" value={leaderSearch} onChange={e => { setLeaderSearch(e.target.value); searchLeads(e.target.value, setLeaderSearchResults); }} placeholder="Search lead by name/phone..." data-testid="group-leader-search" />
                    </div>
                    {leaderSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {leaderSearchResults.map(l => (
                          <button key={l.lead_id} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm" onClick={() => {
                            setGroupForm(f => ({...f, group_leader_lead_id: l.lead_id}));
                            setLeaderSearch(l.name);
                            setLeaderSearchResults([]);
                          }}>
                            <Crown size={12} className="text-amber-500 shrink-0" />
                            <span className="font-medium">{l.name}</span>
                            <span className="text-xs text-slate-400">{l.lead_uid}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {groupForm.group_leader_lead_id && !leaderSearch && (
                      <p className="text-xs text-slate-500 mt-1">Leader: {groupForm.group_leader_lead_id === leadId ? lead?.full_name || 'Current Lead' : groupForm.group_leader_lead_id}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-500 uppercase">Group Notes</Label>
                  <Textarea value={groupForm.group_notes} onChange={e => setGroupForm(f => ({...f, group_notes: e.target.value}))} placeholder="Any notes about this group..." rows={2} />
                </div>
                <Button onClick={groupData ? updateGroup : createGroup} className="bg-slate-900 hover:bg-slate-800" data-testid="save-group-btn">
                  <Save size={14} className="mr-1" /> {groupData ? 'Update Group' : 'Create Group'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Group Info Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="border-slate-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Group Booking</p>
                    <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Group Name</p>
                    <p className="text-sm font-semibold text-slate-900">{groupData.group_name}</p>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Your Role</p>
                    <Badge className={`text-xs capitalize ${groupRole === 'leader' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                      {groupRole === 'leader' ? 'Leader' : 'Member'}
                    </Badge>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Group Size</p>
                    <p className="text-sm font-semibold text-slate-900">{groupData.group_size}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Group Leader */}
              {groupData.leader && (
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2"><Crown size={14} className="text-amber-500" /> Group Leader</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" onClick={() => { if (groupData.leader.lead_id !== leadId) navigate(`/leads/${groupData.leader.lead_id}`); }} data-testid="group-leader-card">
                      <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
                        <Crown size={16} className="text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{groupData.leader.name} {groupData.leader.lead_id === leadId && <span className="text-xs text-slate-400">(This lead)</span>}</p>
                        <p className="text-xs text-slate-400">{groupData.leader.lead_uid} &middot; {groupData.leader.course_name} &middot; {groupData.leader.phone}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Group Notes */}
              {groupData.group_notes && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                  <span className="font-semibold text-slate-500 text-xs uppercase">Notes:</span> {groupData.group_notes}
                </div>
              )}

              {/* Group Members */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2"><Users size={14} /> Group Members ({groupData.group_size})</CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={startGroupEdit} data-testid="edit-group-btn"><Pencil size={12} className="mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={deleteGroup} data-testid="delete-group-btn"><Trash2 size={12} className="mr-1" /> Delete</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Add Member Search */}
                  <div className="relative">
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                      <Input className="pl-8 h-9 text-sm" value={leadSearch} onChange={e => { setLeadSearch(e.target.value); searchLeads(e.target.value, setLeadSearchResults); }} placeholder="Search leads to add to group..." data-testid="add-member-search" />
                    </div>
                    {leadSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {leadSearchResults.filter(l => !groupData.member_lead_ids.includes(l.lead_id)).map(l => (
                          <button key={l.lead_id} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between text-sm" onClick={() => addGroupMember(l.lead_id)}>
                            <div className="flex items-center gap-2">
                              <UserCheck size={12} className="text-green-500 shrink-0" />
                              <span className="font-medium">{l.name}</span>
                              <span className="text-xs text-slate-400">{l.lead_uid}</span>
                            </div>
                            <Plus size={14} className="text-green-500" />
                          </button>
                        ))}
                        {leadSearchResults.filter(l => !groupData.member_lead_ids.includes(l.lead_id)).length === 0 && (
                          <p className="px-3 py-2 text-xs text-slate-400">All results are already in group</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Members List */}
                  <div className="divide-y divide-slate-100">
                    {(groupData.members || []).map(m => (
                      <div key={m.lead_id} className="flex items-center gap-3 py-2.5 group" data-testid={`group-member-${m.lead_uid}`}>
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold ${m.lead_id === groupData.group_leader_lead_id ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                          {m.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button className="text-sm font-medium text-slate-900 hover:text-blue-600 hover:underline" onClick={() => { if (m.lead_id !== leadId) navigate(`/leads/${m.lead_id}`); }}>
                              {m.name} {m.lead_id === leadId && <span className="text-xs text-slate-400">(You)</span>}
                            </button>
                            {m.lead_id === groupData.group_leader_lead_id && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Leader</Badge>}
                          </div>
                          <p className="text-xs text-slate-400 truncate">{m.lead_uid} &middot; {m.course_name} &middot; {m.phone}</p>
                        </div>
                        {m.lead_id !== groupData.group_leader_lead_id && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600" onClick={() => removeGroupMember(m.lead_id)} data-testid={`remove-member-${m.lead_uid}`}>
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    ))}
                    {(!groupData.members || groupData.members.length === 0) && (
                      <p className="text-sm text-slate-400 text-center py-4">No members in this group yet.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-4 space-y-2">
          {activityLogs.map(log => (
            <div key={log.id} className="flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg">
              <div className="w-2 h-2 bg-slate-300 rounded-full mt-2 shrink-0" />
              <div>
                <p className="text-sm text-slate-700">{log.description}</p>
                <p className="text-xs text-slate-400 mt-0.5">{new Date(log.created_at).toLocaleString()} &middot; {log.performed_by_name}</p>
              </div>
            </div>
          ))}
          {activityLogs.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No activity recorded.</p>}
        </TabsContent>
      </Tabs>

      {/* Add Parent Dialog */}
      <Dialog open={addParentDialog} onOpenChange={setAddParentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Parent / Guardian</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={parentForm.relation_type} onValueChange={v => setParentForm(p => ({...p, relation_type: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="father">Father</SelectItem><SelectItem value="mother">Mother</SelectItem><SelectItem value="guardian">Guardian</SelectItem></SelectContent>
            </Select>
            <Input placeholder="Full Name" value={parentForm.full_name} onChange={e => setParentForm(p => ({...p, full_name: e.target.value}))} />
            <Input placeholder="Contact Number" value={parentForm.contact_number} onChange={e => setParentForm(p => ({...p, contact_number: e.target.value}))} />
            <Input placeholder="Email" value={parentForm.email} onChange={e => setParentForm(p => ({...p, email: e.target.value}))} />
            <Input placeholder="Occupation" value={parentForm.occupation} onChange={e => setParentForm(p => ({...p, occupation: e.target.value}))} />
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Annual Income</Label>
              <div className="flex gap-1">
                <Select value={parentForm.income_currency || 'INR'} onValueChange={v => setParentForm(p => ({...p, income_currency: v}))}>
                  <SelectTrigger className="w-24 shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="e.g. 10,00,000" value={fmtIncome(parentForm.annual_income)} onChange={e => setParentForm(p => ({...p, annual_income: rawIncome(e.target.value)}))} />
              </div>
            </div>
            <Button onClick={addParent} className="w-full bg-slate-900 hover:bg-slate-800">Add Parent</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Academic Dialog */}
      <Dialog open={addAcademicDialog} onOpenChange={setAddAcademicDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Academic Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={academicForm.academic_level} onValueChange={v => setAcademicForm(p => ({...p, academic_level: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="class_10">Class 10</SelectItem><SelectItem value="class_12">Class 12</SelectItem><SelectItem value="graduation">Graduation</SelectItem></SelectContent>
            </Select>
            <Input placeholder="School Name" value={academicForm.school_name} onChange={e => setAcademicForm(p => ({...p, school_name: e.target.value}))} />
            <Input placeholder="Board" value={academicForm.board_name} onChange={e => setAcademicForm(p => ({...p, board_name: e.target.value}))} />
            <Select value={academicForm.passing_year || 'unset'} onValueChange={v => setAcademicForm(p => ({...p, passing_year: v === 'unset' ? '' : v}))}>
              <SelectTrigger><SelectValue placeholder="Passing Year" /></SelectTrigger>
              <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Percentage / CGPA" value={academicForm.percentage_or_cgpa} onChange={e => setAcademicForm(p => ({...p, percentage_or_cgpa: e.target.value}))} />
            {academicForm.academic_level !== 'class_10' && (
              <>
                <Input placeholder="Stream" value={academicForm.stream} onChange={e => setAcademicForm(p => ({...p, stream: e.target.value}))} />
                <Input placeholder="Major Subjects (comma separated)" value={academicForm.major_subjects} onChange={e => setAcademicForm(p => ({...p, major_subjects: e.target.value}))} />
              </>
            )}
            <Button onClick={addAcademic} className="w-full bg-slate-900 hover:bg-slate-800">Add Academic Record</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Exam Dialog */}
      <Dialog open={addExamDialog} onOpenChange={setAddExamDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Exam Score</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={examForm.exam_type_id || 'unset'} onValueChange={v => setExamForm(p => ({...p, exam_type_id: v === 'unset' ? '' : v}))}>
              <SelectTrigger><SelectValue placeholder="Select exam type" /></SelectTrigger>
              <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.examTypes.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Score" value={examForm.score} onChange={e => setExamForm(p => ({...p, score: e.target.value}))} />
            <Input placeholder="Rank" value={examForm.rank} onChange={e => setExamForm(p => ({...p, rank: e.target.value}))} />
            <Select value={examForm.attempt_year || 'unset'} onValueChange={v => setExamForm(p => ({...p, attempt_year: v === 'unset' ? '' : v}))}>
              <SelectTrigger><SelectValue placeholder="Attempt Year" /></SelectTrigger>
              <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addExam} className="w-full bg-slate-900 hover:bg-slate-800">Add Exam Score</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Course Preference Dialog */}
      <Dialog open={addCoursePrefDialog} onOpenChange={setAddCoursePrefDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Course Preference</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Priority</Label>
              <Select value={String(coursePrefForm.preference_order || 1)} onValueChange={v => setCoursePrefForm(p => ({...p, preference_order: parseInt(v)}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Primary</SelectItem><SelectItem value="2">Secondary</SelectItem><SelectItem value="3">Tertiary</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Course</Label>
              <Select value={coursePrefForm.course_id || 'unset'} onValueChange={v => setCoursePrefForm(p => ({...p, course_id: v === 'unset' ? '' : v, sub_course_id: ''}))}>
                <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent><SelectItem value="unset">Select</SelectItem>{masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Sub-Course</Label>
              <Select value={coursePrefForm.sub_course_id || 'unset'} onValueChange={v => setCoursePrefForm(p => ({...p, sub_course_id: v === 'unset' ? '' : v}))} disabled={!coursePrefForm.course_id}>
                <SelectTrigger><SelectValue placeholder={coursePrefForm.course_id ? "Select" : "Select course first"} /></SelectTrigger>
                <SelectContent><SelectItem value="unset">None</SelectItem>{(masters.subCourses || []).filter(s => s.course_id === coursePrefForm.course_id).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={addCoursePref} className="w-full bg-slate-900 hover:bg-slate-800">Add Course Preference</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Schedule Item Dialog */}
      <Dialog open={addScheduleDialog} onOpenChange={setAddScheduleDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Payment Schedule Row</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Month / Year *</Label>
              <Select value={scheduleForm.month_year || 'none'} onValueChange={v => setScheduleForm(p => ({...p, month_year: v === 'none' ? '' : v}))}>
                <SelectTrigger data-testid="schedule-month-year"><SelectValue placeholder="Select Month/Year" /></SelectTrigger>
                <SelectContent>{MONTH_YEAR_OPTIONS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Scheduled Amount (₹) *</Label>
              <Input type="number" value={scheduleForm.scheduled_amount} onChange={e => setScheduleForm(p => ({...p, scheduled_amount: e.target.value}))} data-testid="schedule-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Payment Mode</Label>
              <Select value={scheduleForm.payment_mode || 'none'} onValueChange={v => setScheduleForm(p => ({...p, payment_mode: v === 'none' ? '' : v}))}>
                <SelectTrigger data-testid="schedule-mode"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{paymentModes.map(m => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Remarks</Label>
              <Input value={scheduleForm.remarks} onChange={e => setScheduleForm(p => ({...p, remarks: e.target.value}))} />
            </div>
            <Button onClick={addScheduleItem} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="add-schedule-submit">Add to Schedule</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Scholarship Dialog */}
      <Dialog open={scholarshipDialog} onOpenChange={setScholarshipDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Award size={18} className="text-emerald-600" /> Apply Scholarship</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Course</Label>
              <Input value={masters.courses.find(c => c.id === scholarshipForm.course_id)?.name || ''} disabled className="bg-slate-50" data-testid="scholarship-course" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Fees Pulled (₹)</Label>
              <Input value={`₹${Number(scholarshipForm.original_fees || 0).toLocaleString('en-IN')}`} disabled className="bg-slate-50 font-semibold" data-testid="scholarship-fees-pulled" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Scholarship Amount (₹) *</Label>
              <Input type="number" value={scholarshipForm.scholarship_amount} onChange={e => setScholarshipForm(p => ({...p, scholarship_amount: e.target.value}))} placeholder="Enter scholarship amount" data-testid="scholarship-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Final Fees (₹)</Label>
              <Input value={`₹${Number(Math.max(0, (scholarshipForm.original_fees || 0) - (parseFloat(scholarshipForm.scholarship_amount) || 0))).toLocaleString('en-IN')}`} disabled className="bg-emerald-50 font-bold text-emerald-800" data-testid="scholarship-final-fees" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Remarks</Label>
              <Input value={scholarshipForm.remarks} onChange={e => setScholarshipForm(p => ({...p, remarks: e.target.value}))} placeholder="Scholarship reason / notes" data-testid="scholarship-remarks" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Approved By</Label>
              <Select value={scholarshipForm.approved_by || 'none'} onValueChange={v => setScholarshipForm(p => ({...p, approved_by: v === 'none' ? '' : v}))}>
                <SelectTrigger data-testid="scholarship-approved-by"><SelectValue placeholder="Select User" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {masters.users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={applyScholarship} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="apply-scholarship-submit">
              <Award size={14} className="mr-1" /> Apply Scholarship
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Payment Course Change Dialog */}
      <Dialog open={paymentCourseEdit} onOpenChange={setPaymentCourseEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Course</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-3">Changing the course will update the payment plan fees and reset any scholarship. Select a new course below.</p>
          <div className="space-y-2">
            {masters.courses.map(c => (
              <button key={c.id} className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${paymentData?.course_id === c.id ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`} onClick={() => changePaymentCourse(c.id)} data-testid={`select-payment-course-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.code}</p>
                  </div>
                  <p className="text-sm font-semibold text-slate-700">{c.annual_fees ? `₹${Number(c.annual_fees).toLocaleString('en-IN')}` : '—'}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lost Reason Modal */}
      <Dialog open={lostReasonModal} onOpenChange={setLostReasonModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reason for Marking as Lost</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Please select a reason for marking this lead as lost.</p>
          <div className="space-y-3 mt-2">
            <Select value={lostReasonForm.lost_reason_id} onValueChange={v => setLostReasonForm(p => ({ ...p, lost_reason_id: v }))} data-testid="lost-reason-select">
              <SelectTrigger data-testid="lost-reason-trigger"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {masters.lostReasons.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Additional notes (optional)" value={lostReasonForm.lost_notes}
              onChange={e => setLostReasonForm(p => ({ ...p, lost_notes: e.target.value }))}
              className="min-h-[80px]" data-testid="lost-reason-notes" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setLostReasonModal(false); setEditForm(p => ({ ...p, current_stage_id: lead.current_stage_id })); }} data-testid="lost-reason-cancel">Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmLostReason} data-testid="lost-reason-confirm">Confirm Lost</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
